// Compass · SMV sales CRM (React). Boot: finish the Android app's sign-in hand-off, then render.
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { HashRouter } from "react-router";
import "../styles.css";
import { SignedOut, api, post, setSignedOutHandler } from "./lib/api";
import { queryClient } from "./lib/query";
import { SessionProvider } from "./lib/session";
import type { Me } from "./lib/types";
import { Login } from "./features/auth/Login";
import { Shell } from "./features/shell/Shell";
import { Boot } from "./ui/loading";
import { ModalHost, closeModal } from "./ui/modal";
import { Toaster } from "./ui/toast";

setSignedOutHandler(() => {
  closeModal();
  queryClient.setQueryData(["me"], null);
  setTimeout(() => queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" }));
});

function App() {
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api<Me>("/api/auth/me").catch((e) => { if (e instanceof SignedOut) return null; throw e; }),
    staleTime: Infinity,
  });
  const boot = <Boot />;
  if (me.isPending) return boot;
  if (!me.data) return <Login />;
  return (
    <Suspense fallback={boot}>
      <SessionProvider me={me.data}><Shell /><ModalHost /></SessionProvider>
    </Suspense>
  );
}

async function boot() {
  // Android WebView hand-off: /m#code=… (a fragment never reaches server logs) → one-time exchange
  const m = location.hash.match(/[#&?]code=([\w-]+)/);
  if (m) {
    const next = (location.hash.match(/[&?]next=([^&]+)/) || [])[1];
    await post("/api/auth/exchange", { code: m[1] }).catch(() => {}); // a failed exchange lands on sign-in
    const dest = next ? decodeURIComponent(next) : "/today";
    history.replaceState(null, "", location.pathname + "#" + (dest.startsWith("/") ? dest : "/today"));
  }
  createRoot(document.getElementById("app")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <HashRouter><App /></HashRouter>
        <Toaster />
      </QueryClientProvider>
    </StrictMode>,
  );
}
void boot();
